import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import './Settings.css';

const DEFAULT_FORM = {
  mailHost: '',
  mailPort: '',
  mailUsername: '',
  mailPassword: '',
  mailFrom: '',
};

// -------------------------------------------------------------------
// View Tab — read-only display of current SMTP settings
// -------------------------------------------------------------------
function SmtpView({ settings, isConfigured, onEdit }) {
  if (!isConfigured) {
    return (
      <div className="settings-empty">
        <div className="settings-empty-icon">✉️</div>
        <h3>No SMTP configuration yet</h3>
        <p>
          Your company hasn't configured a custom email server. System emails are
          currently sent using the platform default. Click below to add your own.
        </p>
        <button className="btn btn-primary" onClick={onEdit}>
          + Add SMTP Configuration
        </button>
      </div>
    );
  }

  return (
    <div className="smtp-view">
      <div className="smtp-view-status">
        <span className="status-dot status-active" />
        Custom SMTP is active for your company
      </div>
      <div className="smtp-detail-grid">
        <div className="smtp-detail-item">
          <span className="smtp-detail-label">Mail Host</span>
          <span className="smtp-detail-value">{settings.mailHost}</span>
        </div>
        <div className="smtp-detail-item">
          <span className="smtp-detail-label">Mail Port</span>
          <span className="smtp-detail-value">{settings.mailPort}</span>
        </div>
        <div className="smtp-detail-item">
          <span className="smtp-detail-label">Username</span>
          <span className="smtp-detail-value">{settings.mailUsername}</span>
        </div>
        <div className="smtp-detail-item">
          <span className="smtp-detail-label">Password</span>
          <span className="smtp-detail-value smtp-detail-masked">{'•'.repeat(12)}</span>
        </div>
        <div className="smtp-detail-item smtp-detail-full">
          <span className="smtp-detail-label">From Address</span>
          <span className="smtp-detail-value">{settings.mailFrom}</span>
        </div>
      </div>
      <div className="smtp-view-actions">
        <button className="btn btn-primary" onClick={onEdit}>
          ✏️ Edit Configuration
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Configure Tab — editable form
// -------------------------------------------------------------------
function SmtpForm({ initialValues, onSaved, saving, setSaving }) {
  const [form, setForm] = useState(initialValues);
  const [errors, setErrors] = useState({});

  // Keep form in sync if parent reloads settings
  useEffect(() => {
    setForm(initialValues);
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.mailHost.trim())      e.mailHost     = 'Mail host is required';
    const port = Number(form.mailPort);
    if (!String(form.mailPort).trim()) {
      e.mailPort = 'Mail port is required';
    } else if (isNaN(port) || port < 1 || port > 65535) {
      e.mailPort = 'Port must be between 1 and 65535';
    }
    if (!form.mailUsername.trim())  e.mailUsername = 'Username is required';
    if (!form.mailPassword.trim())  e.mailPassword = 'Password is required';
    if (!form.mailFrom.trim()) {
      e.mailFrom = 'From address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.mailFrom.trim())) {
      e.mailFrom = 'Must be a valid email address';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        mailHost:     form.mailHost.trim(),
        mailPort:     Number(form.mailPort),
        mailUsername: form.mailUsername.trim(),
        mailPassword: form.mailPassword,
        mailFrom:     form.mailFrom.trim(),
      };
      const res = await axios.post('/settings/smtp', payload);
      toast.success(res.data?.message || 'SMTP settings saved successfully');
      onSaved(res.data?.data || res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="settings-note">
        <span className="settings-note-icon">ℹ️</span>
        <span>
          Configure your company's outgoing email server. These settings are used for
          password reset emails and notifications. Leave unconfigured to use the platform default.
        </span>
      </div>

      <div className="settings-form-grid">
        <div className="form-group">
          <label htmlFor="mailHost">
            Mail Host <span className="required-star">*</span>
          </label>
          <input
            id="mailHost" name="mailHost" type="text"
            value={form.mailHost} onChange={handleChange}
            placeholder="smtp.gmail.com"
            className={errors.mailHost ? 'input-error' : ''}
            disabled={saving}
          />
          {errors.mailHost
            ? <span className="field-error">{errors.mailHost}</span>
            : <span className="field-hint">e.g. smtp.gmail.com, smtp.office365.com</span>}
        </div>

        <div className="form-group">
          <label htmlFor="mailPort">
            Mail Port <span className="required-star">*</span>
          </label>
          <input
            id="mailPort" name="mailPort" type="number"
            min="1" max="65535"
            value={form.mailPort} onChange={handleChange}
            placeholder="587"
            className={errors.mailPort ? 'input-error' : ''}
            disabled={saving}
          />
          {errors.mailPort
            ? <span className="field-error">{errors.mailPort}</span>
            : <span className="field-hint">Common: 587 (TLS), 465 (SSL), 25</span>}
        </div>

        <div className="form-group">
          <label htmlFor="mailUsername">
            Username <span className="required-star">*</span>
          </label>
          <input
            id="mailUsername" name="mailUsername" type="text"
            autoComplete="username"
            value={form.mailUsername} onChange={handleChange}
            placeholder="your@email.com"
            className={errors.mailUsername ? 'input-error' : ''}
            disabled={saving}
          />
          {errors.mailUsername
            ? <span className="field-error">{errors.mailUsername}</span>
            : <span className="field-hint">Your SMTP account login</span>}
        </div>

        <div className="form-group">
          <label htmlFor="mailPassword">
            Password <span className="required-star">*</span>
          </label>
          <input
            id="mailPassword" name="mailPassword" type="password"
            autoComplete="new-password"
            value={form.mailPassword} onChange={handleChange}
            placeholder="••••••••"
            className={errors.mailPassword ? 'input-error' : ''}
            disabled={saving}
          />
          {errors.mailPassword
            ? <span className="field-error">{errors.mailPassword}</span>
            : <span className="field-hint">For Gmail, use an App Password</span>}
        </div>

        <div className="form-group form-group-full">
          <label htmlFor="mailFrom">
            From Address <span className="required-star">*</span>
          </label>
          <input
            id="mailFrom" name="mailFrom" type="email"
            value={form.mailFrom} onChange={handleChange}
            placeholder="noreply@yourcompany.com"
            className={errors.mailFrom ? 'input-error' : ''}
            disabled={saving}
          />
          {errors.mailFrom
            ? <span className="field-error">{errors.mailFrom}</span>
            : <span className="field-hint">Address shown in outgoing email "From" field</span>}
        </div>
      </div>

      <div className="settings-form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? '💾 Saving…' : '💾 Save Settings'}
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
// Main Settings Page
// -------------------------------------------------------------------
function Settings() {
  const { isCompanyAdmin, hasMenuAccess } = useAuth();
  const [activeTab, setActiveTab] = useState('view');
  const [settings, setSettings] = useState(null);   // null = not loaded yet
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/settings/smtp');
      const data = res.data?.data || res.data;
      const configured = !!data?.id;
      setIsConfigured(configured);
      setSettings({
        mailHost:     data?.mailHost     || '',
        mailPort:     data?.mailPort     || '',
        mailUsername: data?.mailUsername || '',
        mailPassword: data?.mailPassword || '',
        mailFrom:     data?.mailFrom     || '',
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load SMTP settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Redirect non-admins after hooks
  if (!isCompanyAdmin() && !hasMenuAccess('email-settings')) {
    return <Navigate to="/" replace />;
  }

  // After a save, update local state and switch back to view tab
  const handleSaved = (saved) => {
    setIsConfigured(!!saved?.id);
    setSettings({
      mailHost:     saved?.mailHost     || '',
      mailPort:     saved?.mailPort     || '',
      mailUsername: saved?.mailUsername || '',
      mailPassword: saved?.mailPassword || '',
      mailFrom:     saved?.mailFrom     || '',
    });
    setActiveTab('view');
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Email Integration</h1>
        <p>Configure outgoing SMTP server</p>
      </div>

      {/* Settings Card */}
      <div className="settings-card">
        {/* Tab Bar */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'view' ? 'settings-tab-active' : ''}`}
            onClick={() => setActiveTab('view')}
          >
            <span className="settings-tab-icon">✉️</span>
            View SMTP Config
          </button>
          <button
            className={`settings-tab ${activeTab === 'configure' ? 'settings-tab-active' : ''}`}
            onClick={() => setActiveTab('configure')}
          >
            <span className="settings-tab-icon">⚙️</span>
            {isConfigured ? 'Edit SMTP Config' : 'Add SMTP Config'}
          </button>
        </div>

        {/* Tab Content */}
        <div className="settings-tab-content">
          {loading ? (
            <div className="settings-loading">
              <div className="settings-loading-spinner" />
              <p>Loading settings…</p>
            </div>
          ) : activeTab === 'view' ? (
            <SmtpView
              settings={settings}
              isConfigured={isConfigured}
              onEdit={() => setActiveTab('configure')}
            />
          ) : (
            <SmtpForm
              initialValues={settings || DEFAULT_FORM}
              onSaved={handleSaved}
              saving={saving}
              setSaving={setSaving}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
